import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  ArrowRight,
  Building,
  WarningCircle,
  WorkfullyLogoMini,
} from "../../../../../assets/icons";
import UseContext from "../../../../../hooks/useContext";
import ApiDatabase from "../../../../../server";
import { dateToYYYYMMDD } from "../../../../../utils/Date";
import Banner from "../../../../banner/banner";
import Button from "../../../../button";
import StyledInput from "../../../../styledInput/StyledInput";
import SlideOverBody from "../../../component/body";
import SlideOverFooter from "../../../component/footer";
import SlideOverHeader from "../../../component/header";
import useSlideOver from "../../../useSlideOver";

const SlideOverExportMission = () => {
  const { toggleSlideOver } = useSlideOver();
  const { companySelected } = UseContext();
  const [isLoadingExportMission, setIsLoadingExportMission] = useState(false);
  const [errorExportMission, setErrorExportMission] = useState(false);
  const [errorCompany, setErrorCompany] = useState(false);
  const [errorDate, setErrorDate] = useState(false);
  const [errorTypeExport, setErrorTypeExport] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [companySelectedExport, setCompanySelectedExport] =
    useState(companySelected);
  const [dateSelect, setDateSelect] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const start = params.get("dateStart");
    const end = params.get("dateEnd");
    return [start ? new Date(start) : null, end ? new Date(end) : null];
  });
  const [isWaitingCompanies, setIsWaitingCompanies] = useState(true);
  const [typeExportSelected, setTypeExportSelected] = useState("workfully");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [interimAgencyList, setInterimAgencyList] = useState([]);
  const [interimAgencySelected, setInterimAgencySelected] = useState([]);
  const [interimSectorList, setInterimSectorList] = useState([]);
  const [interimSectorSelected, setInterimSectorSelected] = useState([]);
  const [missingShiftsCount, setMissingShiftsCount] = useState(0);
  const dropDownRef = useRef();

  const typeExport = [
    {
      icon: <WorkfullyLogoMini wh={19} />,
      label: "Fichier Workfully",
      description: "Détails des pointages des intérimaire.",
      type: "workfully",
    },
  ];

  useEffect(() => {
    let handler = (e) => {
      if (!dropDownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  });

  const closeSlideOver = () => {
    toggleSlideOver();
  };

  const handleSubmit = () => {
    setIsLoadingExportMission(true);

    const noCompanySelected = !companySelectedExport?.idCompany;
    const noDatesSelected = !dateSelect[0] || !dateSelect[1];
    const noTypeSelected = !typeExportSelected;

    setErrorCompany(
      noCompanySelected ? "Veuillez sélectionner une entreprise" : ""
    );
    setErrorDate(noDatesSelected ? "Veuillez sélectionner une période" : "");
    setErrorTypeExport(
      noTypeSelected ? "Veuillez sélectionner un type d'export" : ""
    );

    const hasError = noCompanySelected || noDatesSelected || noTypeSelected;

    const payload = {
      idInterimAgency: companySelectedExport?.idCompany,
      companies:
        interimAgencySelected.length > 0
          ? JSON.stringify(interimAgencySelected.map((agency) => agency.name))
          : [],
      period: JSON.stringify(dateSelect),
      type: typeExportSelected,
      interimSectors:
        interimSectorSelected.length > 0
          ? JSON.stringify(interimSectorSelected.map((sector) => sector.name))
          : undefined,
    };

    if (noCompanySelected) {
      payload.error = "Aucune entreprise sélectionnée";
    }

    if (hasError) {
      setIsLoadingExportMission(false);
      return;
    }

    ApiDatabase.getExportMissions(
      payload,
      (data) => {
        if (data.timeSheetsCSV) {
          setErrorExportMission(false);
          const blob = new Blob([data.timeSheetsCSV], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const date = new Date();
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          const hours = date.getHours().toString().padStart(2, "0");
          const minutes = date.getMinutes().toString().padStart(2, "0");
          const seconds = date.getSeconds().toString().padStart(2, "0");

          const dateNow = year + month + day + hours + minutes + seconds;

          a.download = `Heures_${dateNow}.csv`;
          a.click();
        } else {
          setErrorExportMission("Aucunes données à exporter");
        }
        setIsLoadingExportMission(false);
      },
      (err) => {
        setIsLoadingExportMission(false);
        console.warn(err);
      }
    );
  };

  useEffect(() => {
    const fetchCompaniesMissions = async () => {
      try {
        setIsWaitingCompanies(true);
        const getInterimAgenciesMissionsPromise = () =>
          new Promise((resolve, reject) => {
            ApiDatabase.getInterimAgenciesMissions(
              {},
              (data) => resolve(data),
              (err) => reject(err)
            );
          });

        const data = await getInterimAgenciesMissionsPromise();

        if (data.interimAgencies?.length > 0) {
          setCompanyList(
            data.interimAgencies.map((company) => ({
              idCompany: company._id,
              name: company.companyName || company.name,
            }))
          );
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsWaitingCompanies(false);
      }
    };

    fetchCompaniesMissions();
  }, [companySelected, companySelectedExport]);

  const updateCompaniesList = (updatedCompany) => {
    const value = updatedCompany.target.value;
    const companySelected = companyList.find(
      (company) => company.idCompany === value
    );

    if (companySelected) {
      setCompanySelectedExport(companySelected);
    }
  };

  useEffect(() => {
    const fetchInterimSectors = async () => {
      try {
        const getInterimSectorsPromise = () =>
          new Promise((resolve, reject) => {
            ApiDatabase.getWorkerSectorMissions(
              {},
              (data) => resolve(data),
              (err) => reject(err)
            );
          });

        const data = await getInterimSectorsPromise();

        if (data.workerSectors?.length > 0) {
          setInterimSectorList(
            data.workerSectors.map((sector) => ({
              name: sector.name,
              checked: false,
            }))
          );
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchInterimSectors();
  }, []);

  useEffect(() => {
    ApiDatabase.getCompaniesMissions(
      {},
      (data) => {
        if (data.companies?.length > 0) {
          const selectedAgencyNames =
            interimAgencySelected?.map((agency) => agency.name) || [];
          const hasPreselected =
            Array.isArray(interimAgencySelected) &&
            interimAgencySelected.length > 0;

          const updatedCompanies = data.companies.map((agency) => ({
            ...agency,
            checked: hasPreselected
              ? selectedAgencyNames.includes(agency.name)
              : true,
          }));

          setInterimAgencyList(updatedCompanies);

          if (!hasPreselected) {
            setInterimAgencySelected(updatedCompanies);
          }
        } else {
          setInterimAgencyList([]);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }, [setInterimAgencySelected, setInterimAgencyList, interimAgencySelected]);

  useEffect(() => {
    if (!dateSelect[0] || !dateSelect[1] || !companySelectedExport?.idCompany) {
      setMissingShiftsCount(0);
      return;
    }

    ApiDatabase.getMissingShiftsCount(
      {
        idInterimAgency: companySelectedExport.idCompany,
        dateFrom: dateToYYYYMMDD(dateSelect[0]),
        dateTo: dateToYYYYMMDD(dateSelect[1]),
      },
      (data) => setMissingShiftsCount(data.count || 0),
      () => setMissingShiftsCount(0)
    );
  }, [dateSelect, companySelectedExport?.idCompany]);

  const updateInterimAgenciesList = (updatedInterimAgency) => {
    const updatedInterimAgencies = [];

    for (let i = 0; i < interimAgencyList.length; i++) {
      for (let j = 0; j < updatedInterimAgency.length; j++) {
        if (interimAgencyList[i].name === updatedInterimAgency[j].value) {
          updatedInterimAgencies.push({
            ...interimAgencyList[i],
            checked: updatedInterimAgency[j].checked,
          });
        }
      }
    }

    setInterimAgencyList(updatedInterimAgencies);

    const selectedInterimAgencies = updatedInterimAgencies.filter(
      (interimAgency) => interimAgency.checked
    );

    setInterimAgencySelected(selectedInterimAgencies);
  };

  const updateInterimSectorsList = (updatedInterimSector) => {
    const updatedInterimSectors = [];

    for (let i = 0; i < interimSectorList.length; i++) {
      for (let j = 0; j < updatedInterimSector.length; j++) {
        if (interimSectorList[i].name === updatedInterimSector[j].value) {
          updatedInterimSectors.push({
            ...interimSectorList[i],
            checked: updatedInterimSector[j].checked,
          });
        }
      }
    }

    setInterimSectorList(updatedInterimSectors);

    const selectedInterimSectors = updatedInterimSectors.filter(
      (interimSector) => interimSector.checked
    );

    setInterimSectorSelected(selectedInterimSectors);
  };

  return (
    <>
      <SlideOverHeader hide={closeSlideOver} hideShow={true}>
        <div className={"text-lg"}>Exporter les pointages</div>
        <div className={"flex gap-1 text-sm"}>
          <div className={"text-gray-500"}>
            Exportez les pointages sur une période donnée pour l'enseigne
          </div>
          <div className={"font-black"}>{companySelectedExport.name}.</div>
        </div>
      </SlideOverHeader>
      <SlideOverBody>
        <div className={"flex flex-col gap-4 h-full"}>
          {missingShiftsCount > 0 && dateSelect[0] && dateSelect[1] && (
            <Banner type="alert" hideIcon>
              <div className="flex flex-col gap-1">
                <div className={"font-bold text-base text-red-800"}>Shift manquant</div>
                <div>
                  Uniquement les shifts terminées peuvent être exportés via
                  Peopulse. Pour la période sélectionnée, il y a{" "}
                  {missingShiftsCount} shift{missingShiftsCount > 1 ? "s" : ""}{" "}
                  manquant{missingShiftsCount > 1 ? "s" : ""}.
                </div>
                <a
                  href={`/hourly-tracking?companySelected=${companySelectedExport?.idCompany || ""}&dateStart=${dateSelect[0].toISOString()}&dateEnd=${dateSelect[1].toISOString()}&missionStatusList=Shift+manquant`}
                  className={"flex items-center justify-end gap-1 font-medium mt-1"}
                >
                  Consulter sur la période
                  <ArrowRight wh={16} color={"#991B1B"} />
                </a>
              </div>
            </Banner>
          )}
          {!isWaitingCompanies ? (
            <>
              {companyList.length > 1 && (
                <div className={"text-sm text-gray-700"}>
                  <StyledInput
                    type={"select"}
                    label={"Établissement"}
                    imgFront={<Building wh={20} color={"#6B7280"} />}
                    value={companySelectedExport?.idCompany || ""}
                    list={companyList.map((company) => {
                      return {
                        value: company.idCompany,
                        label: company.name,
                        checked: company.checked,
                      };
                    })}
                    error={errorCompany}
                    onChange={(updatedCompany) =>
                      updateCompaniesList(updatedCompany)
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <div className={"h-12 bg-gray-200 rounded"}></div>
          )}
          <div className={"flex flex-col gap-1 text-sm"}>
            <label className={"flex flex-col gap-1"}>
              <div className={"text-gray-500"}>Période</div>
              <DatePicker
                dateFormat="dd/MM/yyyy"
                onChange={(dates) => setDateSelect(dates)}
                startDate={dateSelect[0]}
                endDate={dateSelect[1]}
                locale="fr"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperPlacement="bottom"
                className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-sm"
                selectsRange={true}
                placeholderText="Sélectionnez"
                maxDate={new Date()}
              />
            </label>
            {errorDate && (
              <div className={"text-red-500 flex items-center gap-1 text-xs"}>
                <WarningCircle wh={16} color={"#EF4444"} />
                {errorDate}
              </div>
            )}
          </div>
          {interimAgencyList.length > 1 && (
            <div className={"text-sm"}>
              <StyledInput
                label={"Entreprise"}
                type={"selectMultipleCheckbox"}
                placeholderText="Sélectionnez"
                value={
                  interimAgencySelected.length > 0
                    ? interimAgencySelected
                        .map((agency) => agency.name)
                        .join(", ")
                    : ""
                }
                list={interimAgencyList.map((interimAgency) => {
                  return {
                    value: interimAgency.name,
                    label: interimAgency.name,
                    checked: interimAgency.checked,
                  };
                })}
                onChange={(updatedInterimAgency) =>
                  updateInterimAgenciesList(updatedInterimAgency)
                }
                isSearchable={true}
                enableSelectAll={true}
              />
            </div>
          )}
          {interimSectorList.length > 0 && (
            <div className={"text-sm"}>
              <StyledInput
                label={"Secteur (optionnel)"}
                type={"selectMultipleCheckbox"}
                value={
                  interimSectorSelected.length > 0
                    ? interimSectorSelected.map((sector) => sector.name).join(", ")
                    : ""
                }
                list={interimSectorList.map((interimSector) => {
                  return {
                    value: interimSector.name,
                    label: interimSector.name,
                    checked: interimSector.checked,
                  };
                })}
                onChange={(updatedInterimSector) =>
                  updateInterimSectorsList(updatedInterimSector)
                }
                isSearchable={true}
              />
            </div>
          )}
          <div
            className={"relative"}
            ref={dropDownRef}
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <label className={"flex flex-col gap-1 text-sm"}>
              <div className={"text-gray-500"}>Type d'export</div>
              <div className={"border rounded-lg px-4 py-2"}>
                {typeExportSelected ? (
                  typeExport.find(
                    (typeExp) => typeExp.type === typeExportSelected
                  ).label
                ) : (
                  <div className={"text-gray-500"}>Sélectionnez</div>
                )}
              </div>
              {errorTypeExport && (
                <div className={"text-red-500 flex items-center gap-1 text-xs"}>
                  <WarningCircle wh={16} color={"#EF4444"} />
                  {errorTypeExport}
                </div>
              )}
            </label>
            {dropdownOpen && (
              <div
                className={`absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg h-30 w-full top-16 right-0 z-50 bg-gray-50 cursor-pointer`}
              >
                {typeExport.map((typeExp, index) => (
                  <div
                    key={index}
                    className={
                      "flex gap-4 items-center px-4 py-2 border-b last:border-b-0 last:rounded-b-lg"
                    }
                    onClick={() => {
                      setTypeExportSelected(typeExp.type);
                      setDropdownOpen(false);
                    }}
                  >
                    <div
                      className={
                        "w-[28px] h-[28px] flex justify-center items-center border rounded"
                      }
                    >
                      {typeExp.icon}
                    </div>
                    <div>
                      <div className={"text-sm"}>{typeExp.label}</div>
                      <div className={"text-xs text-gray-500"}>
                        {typeExp.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {errorExportMission && (
            <div className={"flex gap-1 items-center text-sm text-red-500"}>
              <WarningCircle wh={16} color={"#EF4444"} />
              <div>{errorExportMission}</div>
            </div>
          )}
        </div>
      </SlideOverBody>
      <SlideOverFooter>
        <Button size={"SM"} color={"SECONDARY"} onClick={closeSlideOver}>
          Fermer
        </Button>
        {isLoadingExportMission ? (
          <Button size={"SM"} color={"DISABLED"}>
            Confirmation...
          </Button>
        ) : (
          <Button size={"SM"} color={"PRIMARY"} onClick={handleSubmit}>
            Confirmer
          </Button>
        )}
      </SlideOverFooter>
    </>
  );
};

export default SlideOverExportMission;
